import React from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';

import { SubscriptionLayout } from '../../templates/SubscriptionLayout';
import { PageWrapper } from '../../atoms/PageWrapper';
import { Button } from '../../atoms/Button';

import { LEGAL_LINK } from '../../../const/Link';
import { AccountSubscriptionSchedule } from '../../organisms/AccountSubscriptionSchedule';
import { LOWER_META_TITLE } from '../../../const/Service';

export const AccountSubscriptionConfirm: React.FC = () => {
  const metaTitle = `課金プラン | ${LOWER_META_TITLE}`;

  return (
    <SubscriptionLayout metaTitle={metaTitle}>
      <PageWrapper>
        <Title>プラン変更申請確認</Title>
        <Description>
          プラン変更の申請手続きを確定します。よろしいですか？
          <br />
          プラン変更は即時で切り替わります。
          <br />
          <span>現プランの残存期間に関しては秒割り計算し返金いたします。</span>
        </Description>
        <Plans>
          <Plan>
            <PlanTitle>現在のプラン</PlanTitle>
            <Card>
              <CardTitle>{'ライト1ヶ月プラン'}</CardTitle>
              <Price>
                <span className="num">{(4480).toLocaleString()}</span>
                <span className="unit">円/月</span>
                <span className="tax">税込</span>
              </Price>
              <CardTitle>契約期間</CardTitle>
              <Date>2022/11/01 16:07:05 ~ 2022/12/01 16:07:05</Date>
              <ServiceTitle>ご利用可能なサービス</ServiceTitle>
              <Service>
                <div>
                  <dl>
                    <dt>教材利用</dt>
                    <dd>無制限</dd>
                  </dl>
                  <dl>
                    <dt>毎月レッスン</dt>
                    <dd>4回</dd>
                  </dl>
                  <dl>
                    <dt>質問掲示板</dt>
                    <dd>無制限</dd>
                  </dl>
                  <dl>
                    <dt>課題提出機能</dt>
                    <dd>無制限</dd>
                  </dl>
                  <dl>
                    <dt>学習ログ</dt>
                    <dd>無制限</dd>
                  </dl>
                </div>
              </Service>
            </Card>
          </Plan>
          <Arrow />
          <Plan>
            <PlanTitle>変更後のプラン</PlanTitle>
            <Card after>
              <CardTitle>{'ベーシック12ヶ月プラン'}</CardTitle>
              <Price after>
                <span className="num">{(2980).toLocaleString()}</span>
                <span className="unit">円/月</span>
                <span className="tax">税込</span>
              </Price>
              <CardTitle>契約期間</CardTitle>
              <Date>2022/12/01 16:07:05 ~ 2023/12/01 16:07:05</Date>
              <ServiceTitle>ご利用可能なサービス</ServiceTitle>
              <Service>
                <div>
                  <dl>
                    <dt>教材利用</dt>
                    <dd>無制限</dd>
                  </dl>
                  <dl>
                    <dt>毎月レッスン</dt>
                    <dd>4回</dd>
                  </dl>
                  <dl>
                    <dt>質問掲示板</dt>
                    <dd>無制限</dd>
                  </dl>
                  <dl>
                    <dt>課題提出機能</dt>
                    <dd>不可</dd>
                  </dl>
                  <dl>
                    <dt>学習ログ</dt>
                    <dd>無制限</dd>
                  </dl>
                </div>
              </Service>
            </Card>
          </Plan>
          <Note>
            ※ 現在お持ちのレッスンチケット&#91;{3}
            枚&#93;は、引き続き有効期限までご使用いただけます。
            <br />
            「特定商取引法に基づく表記」は
            <a href={LEGAL_LINK} target="_blank">
              こちら
            </a>
            をご覧ください。
          </Note>
        </Plans>

        <StyledAccountSubscriptionSchedule confirm />

        {/* MEMO: 上位プラン変更時のみ表示 */}
        <Claim>
          <ClaimTitle>請求額</ClaimTitle>
          <ClaimDescription>
            プラン変更時の請求額は下記の通りです。
            <br />
            <span>現プランの残存期間に関しては秒割り計算し返金いたします。</span>
          </ClaimDescription>
          <Prices>
            <Dt>新プラン請求金額</Dt>
            <Dd>{(4180).toLocaleString()}円(税込)</Dd>
            <Dt>現プランの残日数</Dt>
            <Dd>{10}日</Dd>
            <Dt>現プランの残日数分差引額</Dt>
            <Dd>{(1493).toLocaleString()}円(税込)</Dd>
            <Dt>合計請求金額</Dt>
            <Dd>{(2687).toLocaleString()}円(税込)</Dd>
          </Prices>
        </Claim>
        <ButtonWrapper>
          <Button gray>取り消す</Button>
          <Button>プラン変更を行う</Button>
        </ButtonWrapper>
      </PageWrapper>
    </SubscriptionLayout>
  );
};

const Title = styled.h2`
  color: rgba(0, 0, 0, 0.87);
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;

  ${media.lessThan('medium')`
    font-size: 1.375rem;
  `}
`;
const Description = styled.p`
  margin-top: 1rem;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1.125rem;
  line-height: 1.5rem;
  text-align: center;

  span {
    color: #eb0000;
    font-size: 1em;
    font-weight: 700;
  }

  ${media.lessThan('medium')`
    font-size: 1rem;
    line-height: 1.8;
  `}
`;
const Plans = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  max-width: 794px;
  margin: 2rem auto 0;

  ${media.lessThan('medium')`
    flex-direction: column;
    gap: .75rem;
  `}
`;
const Plan = styled.div`
  flex: 1;
`;
const Arrow = styled.div`
  display: flex;
  align-items: center;

  &:before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-color: transparent transparent transparent #333;
    border-width: 0.75rem 0 0.75rem 1.25rem;
  }

  ${media.lessThan('medium')`
    justify-content: center;
    &:before {
      border-color: #333 transparent transparent transparent;
      border-width: 1.25rem .75rem 0 .75rem;
    }
  `}
`;
const PlanTitle = styled.h3`
  margin-bottom: 1rem;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.375;

  ${media.lessThan('medium')`
    margin-bottom: .75rem;
  `}
`;
const Card = styled.div<{ after?: boolean }>`
  padding: 1rem;
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 0.375rem;

  ${(props) =>
    props.after &&
    `
    background: #fff9f9;
    border-color: #eb0000;
  `}

  ${media.lessThan('medium')`
    padding: 1rem 1rem 1.5rem;
  `}
`;
const CardTitle = styled.h4`
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: #fff;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.375rem;
  text-align: center;

  ${media.lessThan('medium')`
    margin-bottom: .875rem;
    padding: .25rem;
    line-height: 1.5rem;
  `}
`;
const Price = styled.p<{ after?: boolean }>`
  margin-bottom: 1rem;
  color: rgba(0, 0, 0, 0.87);
  text-align: center;

  .num {
    font-size: 3.75rem;
    font-weight: 500;

    ${(props) =>
      props.after &&
      `
      color: #eb0000;
    `}
  }

  .unit {
    font-size: 1.5rem;
  }

  .tax {
    font-size: 1rem;
  }

  ${media.lessThan('medium')`
    margin-bottom: .875rem;

    .num {
      font-size: 3.25rem;
      font-weight: 700;
    }

    .unit {
      font-size: 1.25rem;
    }

    .tax {
      font-size: .875rem;
    }
  `}
`;
const Date = styled.p`
  margin-top: 0.75rem;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  line-height: 1.5;
  text-align: center;

  ${media.lessThan('medium')`
    margin-top: .625rem;
    font-size: .75rem;
    line-height: 1.25rem;
  `}
`;
const ServiceTitle = styled.h5`
  margin-top: 2rem;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.375;
  text-align: center;

  ${media.lessThan('medium')`
    margin-top: 1.125rem;
  `}
`;
const Service = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: 1rem;

  dl {
    display: flex;
    font-size: 1rem;
    line-height: 1.5rem;

    dt {
      width: 7em;
      font-size: 1em;
    }

    dd {
      flex: 1;
      font-size: 1em;

      &:before {
        content: '：';
      }
    }
  }

  ${media.lessThan('medium')`
    margin-top: .625rem;

    dl {
      font-size: .875rem;
    }
  `}
`;
const Note = styled.p`
  width: 100%;
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.75rem;
  line-height: 1.25rem;

  a {
    color: #1a0dab;
    font-size: 1em;
    text-decoration: underline;
  }
`;

const StyledAccountSubscriptionSchedule = styled(AccountSubscriptionSchedule)`
  margin-top: 4rem;

  ${media.lessThan('medium')`
    margin-top: 3rem;
  `}
`;

const Claim = styled.div`
  max-width: 794px;
  margin: 3rem auto 0;
`;
const ClaimTitle = styled.h3`
  padding: 0.75rem 1rem;
  background: #f5f5f5;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.25rem;

  ${media.lessThan('medium')`
    margin: 0 -1rem;
    padding: .625rem 1rem;
    font-size: 1rem;
  `}
`;
const ClaimDescription = styled.p`
  margin-top: 0.75rem;
  color: rgba(0, 0, 0, 0.87);
  font-size: 0.875rem;
  line-height: 1.25rem;

  span {
    font-size: 1em;
    font-weight: 700;
  }

  ${media.lessThan('medium')`
    margin-top: 1.5rem;
  `}
`;
const Prices = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.5rem;
  margin-top: 1.5rem;

  ${media.lessThan('medium')`
    flex-direction: column;
    gap: 0;
  `}
`;
const Dt = styled.dt`
  width: 200px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;
`;
const Dd = styled.dd`
  width: calc(100% - 208px);
  color: rgba(0, 0, 0, 0.87);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5rem;

  ${media.lessThan('medium')`
    margin-top: .75rem;

    + dt {
      margin-top: 2rem;
    }
  `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 4rem;

  ${media.lessThan('medium')`
    flex-direction: column-reverse;
    margin: 2rem -1rem 0;
    padding: 1rem 1rem 0;
    border-top: 1px solid rgba(0,0,0,.1);
  `}
`;
